import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { QrCode, AlertCircle, Check, Clock, MessageSquare, Lock, PhoneCall } from 'lucide-react';
import { API } from '../App';
import { navigateToHome } from '../lib/navigation';
import AppTopHeader from '../components/AppTopHeader';

const QRScanPage = () => {
  const { qrId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [scannerPhone, setScannerPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showCallConsentModal, setShowCallConsentModal] = useState(false);
  const [showCallAgain, setShowCallAgain] = useState(false);
  const [langTab, setLangTab] = useState('en');

  const getOrCreateScannerId = () => {
    const storageKey = 'sideo_scanner_id';
    const existing = localStorage.getItem(storageKey);
    if (existing && existing.trim()) return existing;

    let generated;
    if (window.crypto?.randomUUID) {
      generated = window.crypto.randomUUID();
    } else {
      generated = `scanner_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    }
    localStorage.setItem(storageKey, generated);
    return generated;
  };

  useEffect(() => {
    const fetchQRStatus = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/q/${qrId}/status`);
        console.log('QRScanPage - Status response:', response.data);
        if (response.data.status === 'unclaimed') {
          navigate(`/q/${qrId}/claim`, { replace: true });
          return;
        }
        setStatus(response.data);
        setError(null);
      } catch (err) {
        console.error('QRScanPage - Error fetching status:', err);
        // Handle different error types
        if (err.response?.status === 404) {
          setError('This QR code does not exist');
        } else {
          setError(err.response?.data?.detail || 'Invalid QR code');
        }
        setStatus(null);
      } finally {
        setLoading(false);
      }
    };

    fetchQRStatus();
  }, [qrId, navigate]);

  const validatePhoneNumber = (phone) => {
    // Remove non-digits
    const cleaned = phone.replace(/\D/g, '');
    // Check 10 digits for India
    if (cleaned.length === 10) return true;
    // Check 12 digits with +91 prefix
    if (cleaned.length === 12 && phone.includes('+91')) return true;
    // Check 11 digits for USA (ignore +1 prefix)
    if (cleaned.length === 11 && phone.includes('+1')) return true;
    return false;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setScannerPhone(value);
    if (value.length > 0 && !validatePhoneNumber(value)) {
      setPhoneError('Enter 10 digits (e.g., 9876543210)');
    } else {
      setPhoneError('');
    }
  };

  const handleAction = () => {
    if (!status) return;

    switch (status.action) {
      case 'show_claim_page':
        navigate(`/q/${qrId}/claim`);
        break;
      case 'show_renewal_page':
        navigate(`/q/${qrId}/renew`);
        break;
      case 'show_notify_page':
        // Stay on notify page flow
        navigate(`/q/${qrId}`);
        break;
      case 'show_disabled_page':
        toast.error(status.message || 'This QR code is disabled');
        break;
      default:
        toast.info(status.message || 'QR status unknown');
    }
  };

  const sendQuickMessage = async (type, content) => {
    setSending(true);
    try {
      const scannerId = getOrCreateScannerId();
      await axios.post(`${API}/messages`, {
        qr_code: qrId,
        message_type: type,
        content,
        scanner_id: scannerId
      });
      toast.success('Message sent to vehicle owner!');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.upgrade_required) {
        toast.error('The car owner has reached their free message limit. Please ask them to upgrade their sideo account.');
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  const requestCallConnection = async () => {
    if (!scannerPhone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (phoneError) {
      toast.error(phoneError);
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/call-request`, {
        qr_code: qrId,
        scanner_phone: scannerPhone,
        consent_to_share_phone: true
      });

      const dialNumber = response?.data?.dial_number;
      if (dialNumber) {
        toast.success('Opening dialer with secure relay number...');
        window.location.href = `tel:${dialNumber}`;
        setShowCallAgain(true);
      } else {
        toast.success(response?.data?.message || 'You will receive a call now. Answer it to connect with the car owner.');
      }
    } catch (error) {
      const status = error.response?.status;
      const errorDetail = error.response?.data?.detail;
      if (status === 429) {
        toast.warning(typeof errorDetail === 'string' ? errorDetail : 'You have an active session. Your call will reach the last QR you scanned.');
      } else if (status === 400) {
        toast.error('Phone must be 10 digits (e.g., 9876543210)');
      } else if (status === 404) {
        toast.error('QR code not recognized. Please try scanning again.');
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to connect call. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleCallButtonClick = () => {
    if (!scannerPhone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (phoneError) {
      toast.error(phoneError);
      return;
    }

    setShowCallConsentModal(true);
  };

  const handleCancelCallConsent = () => {
    setShowCallConsentModal(false);
  };

  const handleConfirmCallConsent = async () => {
    setShowCallConsentModal(false);
    await requestCallConnection();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <QrCode className="w-12 h-12 text-indigo-600 mx-auto mb-4 animate-pulse" />
          <p className="text-lg font-medium text-gray-700">Verifying QR code...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invalid QR Code</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigateToHome(navigate)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-lg transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Render different UIs based on status
  if (status?.status === 'unclaimed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-amber-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="bg-yellow-50 rounded-lg p-4 mb-6 border-2 border-yellow-300">
            <p className="text-sm text-gray-600 text-center mb-1">QR Code ID</p>
            <p className="text-2xl font-mono font-bold text-yellow-600 text-center">{qrId}</p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-6">
            <Clock className="w-6 h-6 text-yellow-500" />
            <p className="text-lg font-semibold text-yellow-700">Ready to Register</p>
          </div>

          <div className="bg-yellow-50 rounded-lg p-4 mb-6 border-l-4 border-yellow-400">
            <p className="text-gray-700">
              This QR code hasn't been registered yet. Register your vehicle to start using this QR code.
            </p>
          </div>

          <button
            onClick={handleAction}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 transform hover:scale-105"
          >
            Register Vehicle Now
          </button>

          <div className="mt-6 text-center text-sm text-gray-500 border-t pt-4">
            <p>No account yet? Create one during registration</p>
          </div>
        </div>
      </div>
    );
  }

  if (status?.status === 'active') {
    const quickMessages = {
      en: [
        { type: 'quick', content: 'Your vehicle is blocking mine', icon: MessageSquare },
        { type: 'quick', content: 'Please move your vehicle', icon: MessageSquare },
        { type: 'quick', content: 'Your headlights are on', icon: MessageSquare },
        { type: 'emergency', content: 'EMERGENCY: Need immediate assistance', icon: AlertCircle },
      ],
      hi: [
        { type: 'quick', content: 'आपकी गाड़ी मेरी गाड़ी के आगे खड़ी है', icon: MessageSquare },
        { type: 'quick', content: 'कृपया अपनी गाड़ी हटा लें', icon: MessageSquare },
        { type: 'quick', content: 'आपकी गाड़ी की हेडलाइट चालू है', icon: MessageSquare },
        { type: 'emergency', content: 'आपातकाल: तुरंत मदद चाहिए', icon: AlertCircle },
      ],
    };
    const activeMessages = quickMessages[langTab];
    const normalizeFeatureFlag = (value, fallback = true) => {
      if (value === undefined) return fallback;
      return !(value === false || value === 'false' || value === 0 || value === '0');
    };

    const ownerAvailable = normalizeFeatureFlag(status?.owner_available, true);
    const callsEnabled = normalizeFeatureFlag(status?.calls_enabled, true);
    const messagesEnabled = normalizeFeatureFlag(status?.messages_enabled, true);
    const isCallUnavailable = !ownerAvailable || !callsEnabled;
    const isMessageUnavailable = !ownerAvailable || !messagesEnabled;
    const callUnavailableMessage = !ownerAvailable
      ? 'Call option is currently unavailable because the vehicle owner is offline.'
      : 'Vehicle owner has turned off the call service.';
    const messageUnavailableMessage = !ownerAvailable
      ? 'Quick messages are currently unavailable because the vehicle owner is offline.'
      : 'Vehicle owner has turned off the message service.';

    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <AppTopHeader hideBackButton />
        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Car Info */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            {status?.vehicle_number ? (
              <>
                <p className="text-sm font-medium text-slate-500 mb-1">Contact Vehicle Owner</p>
                <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow' }}>
                  {status.vehicle_number}
                </h1>
              </>
            ) : (
              <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow' }}>
                Contact Vehicle Owner
              </h1>
            )}
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow' }}>
              Send Quick Message
            </h2>
            <p className="text-sm text-slate-600 mb-2">
              Select quick message to send to vehicle owner.
            </p>
            {/* Language tabs */}
            <div className="flex space-x-2 mb-3">
              <button
                onClick={() => setLangTab('en')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  langTab === 'en'
                    ? 'bg-blue-800 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                English Messages
              </button>
              <button
                onClick={() => setLangTab('hi')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  langTab === 'hi'
                    ? 'bg-blue-800 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                हिंदी संदेश
              </button>
            </div>
            {isMessageUnavailable && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-medium text-amber-800">{messageUnavailableMessage}</p>
              </div>
            )}
            <div className="space-y-3">
              {activeMessages.map((msg, index) => {
                const Icon = msg.icon;
                return (
                  <button
                    key={index}
                    onClick={() => sendQuickMessage(msg.type, msg.content)}
                    disabled={sending || isMessageUnavailable}
                    style={{ fontFamily: 'Barlow' }}
                    className={`w-full p-4 rounded-xl border-2 text-left flex items-center space-x-3 transition-all ${
                      sending || isMessageUnavailable
                        ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                        : msg.type === 'emergency'
                          ? 'border-orange-200 bg-orange-50 hover:bg-orange-100 hover:border-orange-300 hover:-translate-y-1'
                          : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-blue-300 hover:-translate-y-1'
                    }`}
                  >
                    <Icon className={`w-6 h-6 flex-shrink-0 ${
                      sending || isMessageUnavailable
                        ? 'text-slate-400'
                        : msg.type === 'emergency'
                          ? 'text-orange-600'
                          : 'text-blue-800'
                    }`} />
                    <span className={`font-medium ${sending || isMessageUnavailable ? 'text-slate-400' : 'text-slate-900'}`}>
                      {msg.content}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow' }}>
              Call Vehicle Owner
            </h2>
            <p className="text-sm text-slate-500 mb-3">We need your number to connect your call with the vehicle owner.</p>
            {isCallUnavailable && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-medium text-amber-800">{callUnavailableMessage}</p>
              </div>
            )}
            <input
              type="tel"
              value={scannerPhone}
              onChange={handlePhoneChange}
              disabled={isCallUnavailable}
              placeholder="Your phone number (10 digits)"
              className={`w-full rounded-lg border p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all ${
                isCallUnavailable
                  ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                  : phoneError
                    ? 'border-red-500 bg-red-50'
                    : 'border-slate-200'
              }`}
            />
            {phoneError && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
            <div className="flex space-x-3 mt-4">
              <button
                onClick={handleCallButtonClick}
                disabled={sending || !scannerPhone.trim() || phoneError || isCallUnavailable}
                className="w-full bg-[#0DB504] hover:bg-[#0a9e03] disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition flex items-center justify-center gap-2"
              >
                <PhoneCall className="w-5 h-5" />
                Call Vehicle Owner
              </button>
            </div>
            {showCallAgain && (
              <div className="mt-3">
                <button
                  onClick={handleCallButtonClick}
                  disabled={sending || isCallUnavailable}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 border-blue-700 text-blue-800 font-semibold hover:bg-blue-50 transition"
                >
                  <PhoneCall className="w-5 h-5" />
                  Call Again
                </button>
                <p className="text-xs text-slate-500 mt-2 text-center">Owner didn't answer? Tap to retry.</p>
              </div>
            )}
          </div>

          <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <h3 className="text-lg font-bold text-blue-900 mb-2" style={{ fontFamily: 'Barlow' }}>
              Liked this SIDEO experience?
            </h3>
            <p className="text-sm text-blue-800 mb-4">
              Protect your own privacy on the road. Get a SIDEO QR Tag for your vehicle and let people contact you without sharing your number publicly.
            </p>
            <button
              onClick={() => navigate('/buy-qr')}
              className="bg-[#0DB504] hover:bg-[#0a9e03] text-white font-semibold py-2.5 px-4 rounded-lg transition"
            >
              Buy QR Tag
            </button>
          </div>

          {showCallConsentModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
              <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-3" style={{ fontFamily: 'Barlow' }}>
                  Share Number to Continue?
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed mb-6">
                  To connect via call, your phone number will be shared with the vehicle owner.
                </p>
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCancelCallConsent}
                    className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmCallConsent}
                    className="px-4 py-2 rounded-lg bg-[#0DB504] text-white hover:bg-[#0a9e03] transition"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (status?.status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="bg-orange-50 rounded-lg p-4 mb-6 border-2 border-orange-300">
            <p className="text-sm text-gray-600 text-center mb-1">QR Code ID</p>
            <p className="text-2xl font-mono font-bold text-orange-600 text-center">{qrId}</p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-6">
            <AlertCircle className="w-6 h-6 text-orange-500" />
            <p className="text-lg font-semibold text-orange-700">Subscription Expired</p>
          </div>

          {status?.vehicle_number && (
            <div className="bg-orange-50 rounded-lg p-4 mb-6 border-2 border-orange-200">
              <p className="text-sm text-gray-600 mb-2">Vehicle</p>
              <p className="text-xl font-bold text-orange-600">{status.vehicle_number}</p>
            </div>
          )}

          {status?.expiry_date && (
            <div className="bg-red-50 rounded-lg p-4 mb-6 border-l-4 border-red-400">
              <p className="text-sm text-gray-600 mb-1">Subscription Ended On</p>
              <p className="text-lg font-semibold text-red-600">
                {new Date(status.expiry_date).toLocaleDateString()}
              </p>
            </div>
          )}

          <div className="bg-orange-50 rounded-lg p-4 mb-6 border-l-4 border-orange-400">
            <p className="text-sm text-orange-700">
              Your subscription has expired. Renew it to continue using this QR code for messaging.
            </p>
          </div>

          <button
            onClick={handleAction}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
          >
            Renew Subscription
          </button>

          <div className="mt-6 text-center text-sm text-gray-500 border-t pt-4">
            <p>Questions? support@sideo.in</p>
          </div>
        </div>
      </div>
    );
  }

  if (status?.status === 'disabled') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="bg-red-50 rounded-lg p-4 mb-6 border-2 border-red-300">
            <p className="text-sm text-gray-600 text-center mb-1">QR Code ID</p>
            <p className="text-2xl font-mono font-bold text-red-600 text-center">{qrId}</p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-6">
            <Lock className="w-6 h-6 text-red-500" />
            <p className="text-lg font-semibold text-red-700">QR Code Disabled</p>
          </div>

          <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-400">
            <p className="text-red-700 font-medium mb-2">This QR code has been disabled.</p>
            <p className="text-sm text-red-600">
              Only an administrator can re-enable it. Contact support if you believe this is an error.
            </p>
          </div>

          <button
            onClick={() => navigateToHome(navigate)}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-lg transition mt-6"
          >
            Go Home
          </button>

          <div className="mt-6 text-center text-sm text-gray-500 border-t pt-4">
            <p>Need help? support@sideo.in</p>
          </div>
        </div>
      </div>
    );
  }

  if (status?.status === 'not_found') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Invalid QR Code</h1>
          <p className="text-gray-600 mb-6">{status.message || 'This QR code does not exist'}</p>
          <button
            onClick={() => navigateToHome(navigate)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-lg transition"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Unknown status
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Unknown Status</h1>
        <p className="text-gray-600 mb-6">Unable to determine QR code status. Please try again.</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-6 rounded-lg transition"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

export default QRScanPage;
